import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {useAccountContext} from '../../../contexts/AccountContext';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {FormReadOnly} from '../../../components/AutoForm/AutoFormNew';
import {useOVBuilderDetails} from '../hooks/useOVBuilderDetails';
import {useQueryParams} from '../../../hooks/useQueryParams';
import useTitle from '../../../hooks/useTitle';
import Loading from '../../../components/Loading/Loading';
import validateUUID from '../../../utils/validateUUID';
import {BusinessUnitEnum} from '../../../schema-types/globalTypes';
import {OvbuilderDetails_ovrecommendDetails as OvbuilderDetails_ovbuilderDetails} from '../../../schema-types/OvbuilderDetails';
import {isSubmittedToCuro} from '../BuilderGrid/builderColumns';

export type CreateBuilderProps = {
  isSppDA: boolean;
};

export type createBuilderContext = {
  isSPPDA: boolean;
  isSPPAR: boolean;
  // Broad review-level SPP flag (any of DA/AR/RI), mirrors ReviewType.is_spp.
  isSpp: boolean;
  loadingOvRecommend: boolean;
  builderDetails: OvbuilderDetails_ovbuilderDetails | null;
  builderReviewId: string;
  ovBuilderId: string;
  setOvBuilderId: (x: string) => void;
};

export const CreateBuilderContext = React.createContext<createBuilderContext>({
  isSPPDA: false,
  isSPPAR: false,
  isSpp: false,
  loadingOvRecommend: true,
  builderDetails: null,
  builderReviewId: '',
  ovBuilderId: '',
  setOvBuilderId: () => {},
});

export const useBuilderContext = () => {
  const context = useContext(CreateBuilderContext);
  return context;
};

export function CreateBuilderProvider({
  isSppDA: isSPPDA,
  children,
}: CreateBuilderProps & {children: React.ReactNode}) {
  const {account} = useAccountContext();
  const {reviewId, reviewDetails} = useReviewContext();
  useTitle(`Create requirement - ${reviewDetails?.name}`);
  const {
    queryGetOVBuilderDetails,
    ovBuilderData,
    loadingOvBuilder: loadingOvRecommend,
  } = useOVBuilderDetails();
  const [ovBuilderId, setOvBuilderId] = useState<null | string>(null);
  const query = useQueryParams();

  useEffect(() => {
    // uses layout effect instead of useEffect to make sure there isnt a render of a previously viewed recc
    const queryOvBuilderId = query.get('ovrecommendId');
    if (!queryOvBuilderId) {
      return;
    }
    setOvBuilderId(queryOvBuilderId);
  }, [query]);

  useEffect(() => {
    // uses layout effect instead of useEffect to make sure there isnt a render of a previously viewed recc
    if (!ovBuilderId) {
      return;
    }
    queryGetOVBuilderDetails({
      variables: {
        ovrecommendId: validateUUID(ovBuilderId)!,
      },
      fetchPolicy: 'network-only',
    });
  }, [ovBuilderId]);

  const contextVal: createBuilderContext = useMemo(() => {
    return {
      isSPPDA:
        isSPPDA ||
        account?.businessUnit?.partnershipType === BusinessUnitEnum.DA ||
        false,
      isSPPAR:
        isSPPDA ||
        account?.businessUnit?.partnershipType === BusinessUnitEnum.AR ||
        false,
      isSpp: reviewDetails?.isSpp ?? false,
      loadingOvRecommend: loadingOvRecommend && !!ovBuilderId, // only loading if its actually loading and there's an id associated
      builderDetails: ovBuilderData || null,
      builderReviewId: ovBuilderData?.review?.id || reviewId, // if theres already an associated reviewId, use that
      ovBuilderId: ovBuilderId!,
      setOvBuilderId,
    };
  }, [
    account?.businessUnit?.partnershipType,
    reviewDetails?.isSpp,
    loadingOvRecommend,
    ovBuilderId,
    ovBuilderData,
    setOvBuilderId,
  ]);

  if (!ovBuilderData && ovBuilderId) {
    return <Loading entityName={isSPPDA ? 'plan' : 'recommendation'} />;
  }

  return (
    <CreateBuilderContext.Provider value={contextVal}>
      <ShowIfDebugWrapper debugKey="forms">
        Account: {account?.name}. Account curo id: {account?.curoId}. Review id:{' '}
        {reviewId}. Review name: {reviewDetails?.name || ''}
      </ShowIfDebugWrapper>

      <FormReadOnly readOnly={isSubmittedToCuro(ovBuilderData)}>
        {children}
        <Box mt={3} />
      </FormReadOnly>
    </CreateBuilderContext.Provider>
  );
}
